import React from 'react';
import {
  inputFieldVariants,
  type InputFieldVariants,
} from '../../../../utils/forms/input/input';
import styles from '../Input.module.css';
import {
  isIconButtonElement,
  isButtonElement,
  isIconElement,
} from './inputTypeGuards';

/**
 * Width measurement system for input slots and decorative elements.
 * Used by useInputSlotWidths hook to calculate CSS custom properties
 * (--input-start-slot-width, --input-end-slot-width, etc.)
 * This allows the grid layout to adjust padding based on actual slot widths.
 */
let measureWidthCache: WeakMap<HTMLElement, number> | null = null;

/**
 * Start a batch of width measurements with caching.
 * Called at the start of measurement phase to avoid redundant DOM reads.
 * WeakMap automatically cleans up when elements are garbage collected.
 */
export const startMeasureWidthBatch = () => {
  measureWidthCache = new WeakMap();
};

/**
 * End a batch of width measurements and clear the cache.
 * Called after all slot widths have been measured and applied to CSS vars.
 */
export const endMeasureWidthBatch = () => {
  measureWidthCache = null;
};

/**
 * Measure the width of a DOM element using multiple fallback strategies.
 * Handles edge cases where some measurements might be unavailable:
 * 1. offsetWidth (most accurate, includes padding/border)
 * 2. clientWidth (inner content, without border)
 * 3. scrollWidth (full content width, useful for overflow)
 * 4. Max child width (fallback for complex layouts)
 * Caches result within a batch to avoid repeated DOM queries.
 */
export const measureWidth = (ref: React.RefObject<HTMLDivElement>): number => {
  if (!ref.current) return 0;
  if (measureWidthCache) {
    const cached = measureWidthCache.get(ref.current);
    if (typeof cached === 'number') return cached;
  }
  // Fallback strategies ordered by preference
  // Each strategy handles different layout scenarios
  const strategies = [
    () => ref.current?.offsetWidth || 0, // Includes padding + border (most accurate)
    () => ref.current?.clientWidth || 0, // Content width without border
    () => ref.current?.scrollWidth || 0, // Full scrollable width
    () =>
      Array.from(ref.current?.children || []).reduce(
        (max, child) => Math.max(max, child.clientWidth),
        0,
      ), // Measure children if element itself is empty
  ];

  // Try each strategy until we get a meaningful width
  // Skip zero values to avoid returning empty measurements
  for (const strategy of strategies) {
    const width = strategy();
    if (width > 0) {
      if (measureWidthCache) measureWidthCache.set(ref.current, width);
      return width;
    }
  }

  // All strategies returned 0 (element is hidden/empty)
  if (measureWidthCache) measureWidthCache.set(ref.current, 0);
  return 0;
};

/**
 * Clone and propagate disabled state to all children, adapting for each element type.
 * When the Input is disabled, this function ensures:
 * - Button components get isDisabled prop
 * - Icon components get color='disabledPrimary' (grayed out)
 * - IconButton components get isDisabled prop
 * - Native HTML elements (input, select, textarea) get disabled attribute
 * - Unknown components get data-disabled attribute
 *
 * This ensures consistent disabled styling across all slot content (prefixes, suffixes, buttons, icons).
 */
export const passthroughChildrenWithDisabledState = (
  children: React.ReactNode,
  isDisabled?: boolean,
) => {
  const getIconEndProps = (child: React.ReactElement) => {
    if (!child.props.iconEndProps) return {};
    if (isDisabled) {
      return {
        iconEndProps: {
          ...child.props.iconEndProps,
          color: 'disabledPrimary',
        },
      };
    }
    return { iconEndProps: child.props.iconEndProps };
  };

  const isInteractiveElement = (type: unknown) =>
    typeof type === 'string' &&
    ['button', 'input', 'select', 'textarea'].includes(type);

  return React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;

    if (isButtonElement(child)) {
      return React.cloneElement(child, {
        isDisabled,
        ...getIconEndProps(child),
      });
    }

    if (isIconElement(child)) {
      return React.cloneElement(child, {
        color: isDisabled ? 'disabledPrimary' : child.props.color,
      });
    }

    if (isIconButtonElement(child)) {
      return React.cloneElement(child, {
        isDisabled,
      });
    }

    if (isInteractiveElement(child.type)) {
      return React.cloneElement(child, {
        disabled: isDisabled,
      });
    }

    // Add 'data-disabed' if we don't know the element's type
    return React.cloneElement(child, {
      'data-disabled': isDisabled,
    });
  });
};

/**
 * Detect if a slot contains interactive button elements.
 * Used to conditionally apply padding and layout adjustments when buttons are present.
 * Returns true if any child is a Button or IconButton component.
 */
export const hasButtonOrIconButton = (
  slot: React.ReactElement | undefined,
): boolean => {
  if (!slot) return false;
  const slotChildren = React.Children.toArray(slot.props.children);
  return slotChildren.some(
    (child) => isButtonElement(child) || isIconButtonElement(child),
  );
};

/**
 * Handle special configuration for search inputs (type="search").
 * Search inputs have browser-native clear buttons that we want to hide
 * since the Input component provides its own clearable functionality.
 * This function:
 * - Detects if type is 'search'
 * - Forces clearable=true for search inputs (standard UX)
 * - Applies CSS class to hide browser's native search clear button
 */
export const resolveSearchInputConfig = (
  clearable: boolean,
  inputType: string | undefined,
) => {
  const baseStyle = styles['input-field__input'];
  if (inputType !== 'search') {
    return {
      effectiveClearable: clearable,
      inputFieldType: inputType,
      inputFieldStyle: baseStyle,
    };
  }
  return {
    effectiveClearable: true,
    inputFieldType: 'search' as typeof inputType,
    inputFieldStyle: `${baseStyle} ${styles['input-field__input--hideSearchClear']}`,
  };
};

/**
 * Generate the complete class list for the input wrapper based on responsive column/width config.
 * This uses CVA (Class Variance Authority) to generate the base class from responsive props,
 * then combines it with responsive breakpoint-specific classes.
 *
 * Example output for fieldColumns={{ base: 'twelve', md: 'eight' }}:
 * - baseClass: "input-field-variant input-field-variant--columns-twelve"
 * - responsiveClasses: ["md:input-field-variant--columns-eight"]
 *
 * The browser's CSS media queries automatically switch between base and responsive classes.
 */
export const resolveInputFieldClasses = (
  fieldColumns: unknown,
  fieldMaxWidthByChars: unknown,
  resolvedColumns: InputFieldVariants['fieldColumns'],
  resolvedMaxWidthByChars: InputFieldVariants['fieldMaxWidthByChars'],
  columnsResp: string[],
  maxWidthByCharsResp: string[],
): { baseClass: string; responsiveClasses: string[] } => {
  if (fieldColumns && fieldMaxWidthByChars) {
    return {
      baseClass: inputFieldVariants({
        fieldColumns: resolvedColumns,
        fieldMaxWidthByChars: resolvedMaxWidthByChars,
      }),
      responsiveClasses: [...columnsResp, ...maxWidthByCharsResp],
    };
  }
  if (fieldColumns) {
    return {
      baseClass: inputFieldVariants({ fieldColumns: resolvedColumns }),
      responsiveClasses: columnsResp,
    };
  }
  if (fieldMaxWidthByChars) {
    return {
      baseClass: inputFieldVariants({
        fieldMaxWidthByChars: resolvedMaxWidthByChars,
      }),
      responsiveClasses: maxWidthByCharsResp,
    };
  }
  return { baseClass: inputFieldVariants({}), responsiveClasses: [] };
};

/**
 * Measure all input slot widths and apply them as CSS custom properties.
 * Called by useInputSlotWidths hook to update layout dynamically.
 *
 * This function:
 * 1. Measures each slot element (startSlot, endSlot, prefix, suffix, etc.)
 * 2. Determines the effective start/end width (priority: slot > prefix/suffix > select > icon)
 * 3. Applies --input-start-slot-width and --input-end-slot-width CSS vars
 * 4. Sets data-start-slot and data-end-slot attributes for CSS selectors
 *
 * The CSS grid uses these values to adjust padding and spacing dynamically.
 */
export const measureSlotWidths = (
  wrapperRef: React.RefObject<HTMLDivElement>,
  refs: {
    startSlotRef: React.RefObject<HTMLDivElement>;
    endSlotRef: React.RefObject<HTMLDivElement>;
    prefixRef: React.RefObject<HTMLDivElement>;
    suffixRef: React.RefObject<HTMLDivElement>;
    startSelectRef: React.RefObject<HTMLDivElement>;
    endSelectRef: React.RefObject<HTMLDivElement>;
    startIconRef: React.RefObject<HTMLDivElement>;
    endIconRef: React.RefObject<HTMLDivElement>;
  },
) => {
  const wrapper = wrapperRef.current;
  if (!wrapper) return;

  startMeasureWidthBatch();

  // Measure all potential slot elements (only one will actually be present)
  const startSlotWidth = measureWidth(refs.startSlotRef);
  const endSlotWidth = measureWidth(refs.endSlotRef);
  const prefixWidth = measureWidth(refs.prefixRef); // Text-only prefix (lower precedence)
  const suffixWidth = measureWidth(refs.suffixRef); // Text-only suffix (lower precedence)
  const startSelectWidth = measureWidth(refs.startSelectRef); // Dropdown at start
  const endSelectWidth = measureWidth(refs.endSelectRef); // Dropdown at end
  const startIconWidth = measureWidth(refs.startIconRef); // Icon at start (lowest precedence)
  const endIconWidth = measureWidth(refs.endIconRef); // Icon at end (lowest precedence)

  endMeasureWidthBatch();

  // Priority: Slot > Select > Prefix/Suffix > Icon
  // Use the highest-priority element present, or 0 if nothing exists
  const startWidth =
    startSlotWidth || prefixWidth || startSelectWidth || startIconWidth;
  if (startWidth > 0) {
    // Apply CSS custom property used by grid layout for padding calculation
    wrapper.style.setProperty('--input-start-slot-width', `${startWidth}px`);
    // Set data attribute for CSS selectors like .input-field__wrapper[data-start-slot]
    wrapper.dataset.startSlot = 'true';
  } else {
    // No start element exists, remove the CSS variable
    wrapper.style.removeProperty('--input-start-slot-width');
    delete wrapper.dataset.startSlot;
  }

  // Same logic for end slot
  const endWidth =
    endSlotWidth || suffixWidth || endSelectWidth || endIconWidth;
  if (endWidth > 0) {
    wrapper.style.setProperty('--input-end-slot-width', `${endWidth}px`);
    wrapper.dataset.endSlot = 'true';
  } else {
    wrapper.style.removeProperty('--input-end-slot-width');
    delete wrapper.dataset.endSlot;
  }
};

/**
 * Clear the input value when the clear button is clicked.
 * Triggers the onChange callback with an empty string,
 * then focuses the input field for immediate re-entry.
 */
export const handleClearClick = (
  e: React.MouseEvent,
  inputElementRef: React.MutableRefObject<HTMLInputElement | null>,
  inputProps: Record<string, unknown> | undefined,
) => {
  e.preventDefault();
  e.stopPropagation();
  if (!inputElementRef.current) return;
  inputElementRef.current.focus();
  if (inputProps && 'value' in inputProps) {
    const onChange = inputProps.onChange as
      | ((e: React.ChangeEvent<HTMLInputElement>) => void)
      | undefined;
    onChange?.({
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>);
  }
};

/**
 * Assign the actual input DOM element to both refs:
 * 1. Forwarded ref - exposed to parent component consumers
 * 2. Internal ref - used internally for focus/clear operations
 * Handles both callback refs and RefObject refs for maximum compatibility.
 */
export const assignInputRefs = (
  el: HTMLInputElement | null,
  forwardedRef: React.ForwardedRef<HTMLInputElement>,
  inputElementRef: React.MutableRefObject<HTMLInputElement | null>,
) => {
  if (typeof forwardedRef === 'function') {
    forwardedRef(el);
  } else if (forwardedRef && 'current' in forwardedRef) {
    (forwardedRef as React.MutableRefObject<HTMLInputElement | null>).current =
      el;
  }
  inputElementRef.current = el;
};

/**
 * Render the appropriate start-side element based on priority.
 * Priority order (render only one, in this order):
 * 1. StartIcon - single icon (smallest space)
 * 2. StartSelect - dropdown selector
 * 3. StartSlot - generic custom content
 * 4. Prefix - text-only label
 *
 * Clones the element and attaches the corresponding ref and visuallyHiddenLabel props.
 */
export const resolveStartContent = (
  startIconChildren: React.ReactElement | undefined,
  startSelectChildren: React.ReactElement | undefined,
  startSlotChildren: React.ReactElement | undefined,
  prefixChildren: React.ReactElement | undefined,
  refs: {
    startIconRef: React.RefObject<HTMLDivElement>;
    startSelectRef: React.RefObject<HTMLDivElement>;
    startSlotRef: React.RefObject<HTMLDivElement>;
    prefixRef: React.RefObject<HTMLDivElement>;
  },
  visuallyHiddenLabel: string | undefined,
): React.ReactElement | null => {
  if (startIconChildren)
    return React.cloneElement(startIconChildren, { ref: refs.startIconRef });
  if (startSelectChildren)
    return React.cloneElement(startSelectChildren, {
      ref: refs.startSelectRef,
    });
  if (startSlotChildren)
    return React.cloneElement(startSlotChildren, { ref: refs.startSlotRef });
  if (prefixChildren)
    return React.cloneElement(prefixChildren, {
      ref: refs.prefixRef,
      visuallyHiddenLabel,
    });
  return null;
};

/**
 * Render the appropriate end-side element based on priority.
 * Priority order (render only one, in this order):
 * 1. EndIcon - single icon (smallest space)
 * 2. EndSelect - dropdown selector
 * 3. EndSlot - generic custom content
 * 4. Suffix - text-only label
 *
 * Clones the element and attaches the corresponding ref and visuallyHiddenLabel props.
 */
export const resolveEndContent = (
  endIconChildren: React.ReactElement | undefined,
  endSelectChildren: React.ReactElement | undefined,
  endSlotChildren: React.ReactElement | undefined,
  suffixChildren: React.ReactElement | undefined,
  refs: {
    endIconRef: React.RefObject<HTMLDivElement>;
    endSelectRef: React.RefObject<HTMLDivElement>;
    endSlotRef: React.RefObject<HTMLDivElement>;
    suffixRef: React.RefObject<HTMLDivElement>;
  },
  visuallyHiddenLabel: string | undefined,
): React.ReactElement | null => {
  if (endIconChildren)
    return React.cloneElement(endIconChildren, { ref: refs.endIconRef });
  if (endSelectChildren)
    return React.cloneElement(endSelectChildren, { ref: refs.endSelectRef });
  if (endSlotChildren)
    return React.cloneElement(endSlotChildren, { ref: refs.endSlotRef });
  if (suffixChildren)
    return React.cloneElement(suffixChildren, {
      ref: refs.suffixRef,
      visuallyHiddenLabel,
    });
  return null;
};

/**
 * Determines whether the clear button should be displayed in the input.
 *
 * All four conditions must be true:
 * 1. clearable: Feature is enabled (either user set it or input type is "search")
 * 2. value exists: Value is not null or undefined
 * 3. value is not empty: String representation has length > 0
 * 4. not disabled: Disabled inputs cannot be cleared
 *
 * This logic is extracted for:
 * - Testability: Can unit test independently
 * - Reusability: Other form components (DateField, DatePicker, etc.) can use same logic
 * - Clarity: Makes the intent explicit vs. inline boolean expression
 *
 * @param clearable - Whether clearing is enabled for this input
 * @param value - Current input value (string, number, or null/undefined)
 * @param isDisabled - Whether the input is disabled
 * @returns true if clear button should be visible, false otherwise
 */
export const shouldShowClearButton = (
  clearable: boolean,
  value: string | number | readonly string[] | undefined | null,
  isDisabled: boolean,
): boolean => {
  return clearable && value != null && String(value).length > 0 && !isDisabled;
};
